import { useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";
import { Thread } from "./Thread";
import { ANDREA_PHONE, ANDREA_PHONE_DISPLAY, contactClick } from "../lib/contact";
import { trackEvent } from "../lib/tracking";

// /sleep-plan-review-confirmed — the closing chapter of the paid client
// journey. Calendly redirects here after a parent books their 45-minute
// Sleep Plan Review (the event's confirmation-page setting points at this
// URL on the production domain). Private and noindexed like the rest of
// the journey; no purchase/conversion events fire from this page.
export const ReviewConfirmed = () => {
  const reduce = useReducedMotion();

  useEffect(() => {
    document.title = "You're booked — The Nurtured Parent Co.";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    window.scrollTo(0, 0);
    // Calendly redirects here only after a completed booking — landing on
    // this page IS the reliable scheduled signal, no Calendly API needed.
    trackEvent("sleep_plan_review_scheduled", { source: "calendly_redirect" });
    return () => {
      document.head.removeChild(meta);
      document.title = "The Nurtured Parent Co. — Pediatric Sleep Consulting";
    };
  }, []);

  const rise = (delay) => ({
    initial: reduce ? false : { opacity: 0, y: 22 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <div data-testid="review-confirmed-page" className="min-h-svh bg-ivory text-ink flex flex-col">
      <header className="max-w-[1600px] w-full mx-auto px-6 md:px-12 lg:px-20 py-8">
        <a href="/" data-testid="review-confirmed-logo" className="inline-block">
          <span className="block font-serif font-light text-xl sm:text-2xl leading-tight tracking-tight">
            The Nurtured Parent Co.
          </span>
          <span className="block mt-1 text-[9px] tracking-[0.3em] uppercase text-smoke">
            Pediatric Sleep Consulting
          </span>
        </a>
      </header>

      <main className="flex-1 max-w-[1600px] w-full mx-auto px-6 md:px-12 lg:px-20">
        <section className="py-16 lg:py-24">
          <motion.p {...rise(0.1)} className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
            Sleep Plan Review — confirmed
          </motion.p>
          <motion.h1 {...rise(0.25)} className="mt-6 font-serif font-light text-ink text-5xl sm:text-7xl lg:text-8xl leading-[1.02] tracking-tight">
            You&rsquo;re <span className="italic text-clay">booked.</span>
          </motion.h1>
          <motion.div {...rise(0.4)} className="mt-10 max-w-lg space-y-5 text-base sm:text-lg font-normal leading-relaxed text-smoke">
            <p>
              Your 45-minute Sleep Plan Review is on Andrea&rsquo;s calendar.
              Your confirmation and calendar invite are on their way to your
              inbox — add it to your calendar so it&rsquo;s waiting for you.
            </p>
            <p>
              Until then, Andrea is reviewing everything you shared in your
              intake, so your time together starts with your child — not
              paperwork.
            </p>
          </motion.div>

          <motion.div {...rise(0.55)} data-testid="booking-confirmed" className="mt-12 max-w-lg rounded-3xl border border-ink/10 bg-linen/60 p-6">
            <p className="flex items-center gap-2.5 text-[10px] tracking-[0.25em] uppercase font-semibold text-euca">
              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-euca/15 text-euca">
                <Check size={12} strokeWidth={3} />
              </span>
              Booking confirmed
            </p>
            <p className="mt-3 font-serif font-light text-ink text-2xl tracking-tight">
              Sleep Plan Review
            </p>
            <p className="mt-1 text-sm text-smoke">
              45 minutes · one-on-one with Andrea
            </p>
          </motion.div>
        </section>

        <section className="py-16 lg:py-24 border-t border-ink/10">
          <motion.div {...rise(0.1)} className="max-w-2xl">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
              Before your call
            </p>
            <div className="mt-8">
              {[
                { n: "01", title: "Watch for your confirmation email.", body: "Calendly sends it within a few minutes, with your calendar invite and the option to reschedule if life happens." },
                { n: "02", title: "Jot down your questions.", body: "Nothing is too small. The call is yours — the more you bring, the more you take away." },
                { n: "03", title: "Find a quiet pocket of time.", body: "Forty-five minutes where you can talk freely. Andrea will walk you through your child's plan, step by step." },
              ].map((s, i) => (
                <div key={s.n} data-testid={`prep-step-${i}`} className="py-6 border-t border-ink/10 last:border-b flex items-baseline gap-6">
                  <span aria-hidden="true" className="font-serif italic font-light text-clay text-xl shrink-0">{s.n}</span>
                  <div>
                    <p className="font-serif font-light text-ink text-xl sm:text-2xl leading-snug">{s.title}</p>
                    <p className="mt-1.5 text-sm sm:text-base font-normal leading-relaxed text-smoke">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-8 text-sm font-normal leading-relaxed text-smoke max-w-xl">
              Need to change your time, or something doesn&rsquo;t look right?{" "}
              <a
                data-testid="review-confirmed-text-andrea"
                href={`sms:${ANDREA_PHONE}`}
                onClick={(e) => {
                  trackEvent("text_andrea_click", { location: "review_confirmed" });
                  contactClick("text")(e);
                }}
                className="text-ink underline underline-offset-4 decoration-ink/25 hover:decoration-clay transition-[text-decoration-color] duration-300"
              >
                Text Andrea — {ANDREA_PHONE_DISPLAY}
              </a>
            </p>
          </motion.div>
        </section>
      </main>

      <div className="max-w-[1600px] w-full mx-auto px-6 md:px-12 lg:px-20 pb-10">
        <Thread className="text-clay/40" />
      </div>
    </div>
  );
};
